import { useToken } from '@chakra-ui/react';
import {
  Text,
} from '@ayble-health/dashboard-components';
import { Button } from 'components/ui/button';
import { Check, X } from 'lucide-react';
import { VisitCardButtonType } from './types';
import CalendarSVG from '../../CustomSVGs/CalendarSVG';

const textColorTypeMap = {
  [VisitCardButtonType.Cancel]: 'secondary.200',
  [VisitCardButtonType.Confirm]: '#5299A9',
  [VisitCardButtonType.Confirmed]: 'white',
  [VisitCardButtonType.Reschedule]: 'secondary.200',
};

const textWeightTypeMap = {
  [VisitCardButtonType.Cancel]: 'normal',
  [VisitCardButtonType.Confirm]: 'bold',
  [VisitCardButtonType.Confirmed]: 'normal',
  [VisitCardButtonType.Reschedule]: 'normal',
};

const bgTypeMap = {
  [VisitCardButtonType.Confirmed]: '#5299A9',
};

type Props = {
  loading?: boolean;
  onClick?: (type: VisitCardButtonType) => void;
  type: VisitCardButtonType;
};

/**
 * Action button rendered inside `VisitCard`. Renders the right
 * icon + label per `VisitCardButtonType` (Confirm / Confirmed /
 * Reschedule / Cancel) with type-specific text/bg colors.
 *
 * `Confirmed` is a static read-only pill — it ignores `onClick` and
 * suppresses pointer events.
 *
 * Not intended for use outside `VisitCard`.
 *
 * @example
 * <VisitCardButton
 *   type={VisitCardButtonType.Reschedule}
 *   loading={isLoading}
 *   onClick={reschedule}
 * />
 */
export const VisitCardButton = ({ loading, onClick, type }: Props) => {
  const canClick = type !== VisitCardButtonType.Confirmed;
  const [secondary200] = useToken('colors', ['secondary.200']);

  const iconTypeMap = {
    [VisitCardButtonType.Cancel]: <X color={secondary200} size={12} />,
    [VisitCardButtonType.Confirm]: <Check color={secondary200} size={16} />,
    [VisitCardButtonType.Reschedule]: <CalendarSVG />,
  };

  return (
    <Button
      bgColor={bgTypeMap[type]}
      loading={loading}
      maxH={'24px'}
      minH={0}
      onClick={() => onClick?.(type)}
      paddingX={'12px'}
      paddingY={'6px'}
      pointerEvents={canClick ? undefined : 'none'}
      variant={'plain'}
    >
      {iconTypeMap[type]}
      <Text
        color={textColorTypeMap[type]}
        fontSize={'xs'}
        fontWeight={textWeightTypeMap[type]}
        lineHeight={'12px'}
      >
        {type}
      </Text>
    </Button>
  );
};
